<script setup lang="ts">
import { computed, onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useLocaleStore } from '@/stores/locale'
import { useCookieConsent } from '@/features/cookie-consent/composables/useCookieConsent'
import { appInitializer } from '@/services/app/appInitializer.service'
import { recordSiteView } from '@/services/app/siteView.service'
import CookieConsentDialog from '@/features/cookie-consent/components/CookieConsentDialog.vue'
import AiChatbotWidget from '@/features/ai-chatbot/components/AiChatbotWidget.vue'

// Загружаем сохранённую локаль при старте приложения
const localeStore = useLocaleStore()
const { locale: i18nLocale } = useI18n()

// DEV-1015: обезличенный лендинг презентации агента (/p/:token) не должен нести
// брендинг/чрому VINPROP — прячем глобальные AI-чат и cookie-диалог на этом роуте.
const route = useRoute()
const isBrandedChromeHidden = computed(() => route.name === 'presentation-landing')

// Cookie Consent
const { shouldShowDialog, loadConsent, hideDialog } = useCookieConsent()

onMounted(async () => {
  // Инициализируем приложение (featured комплексы, filter values)
  // Это запускается параллельно и не блокирует UI
  appInitializer.initialize()

  // DEV-1857: просмотр сайта в отчёт по трафику. Один маячок на загрузку
  // приложения, переходы роутера не считаются: иначе одна сессия давала бы
  // десяток «просмотров», и конверсия сайта выглядела бы вдесятеро хуже.
  // Презентации агентов сервис пропускает сам.
  void recordSiteView()
  
  localeStore.loadSavedLocale()
  // Синхронизируем i18n с locale store
  i18nLocale.value = localeStore.currentLocale
  // Загружаем согласие на cookies
  loadConsent()
})

// Синхронизируем изменения локали с vue-i18n
watch(
  () => localeStore.currentLocale,
  newLocale => {
    i18nLocale.value = newLocale
  }
)
</script>

<template>
  <RouterView />
  <CookieConsentDialog
    v-if="shouldShowDialog && !isBrandedChromeHidden"
    @close="hideDialog"
  />
  <AiChatbotWidget v-if="!isBrandedChromeHidden" />
</template>

<style>
/* Глобальные стили */
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

html,
body {
  background: white;
  color: #1f2937;
  overflow-x: clip; /* Убираем горизонтальный скролл без создания scroll context (не ломает sticky) */
}

#app {
  min-height: 100vh;
  background: white;
  overflow: visible; /* Не создаём scroll context */
}

/* Стили для placeholder views */
.placeholder-view {
  max-width: 1280px;
  margin: 0 auto;
  padding: 2rem;
  text-align: center;
}

.placeholder-view h1 {
  margin-bottom: 1rem;
  color: #1e40af;
}

.placeholder-view p {
  color: #6b7280;
}
</style>
